import { useTheme } from '@mui/material/styles';
import { SEARCH, FONTS, DISPLAY_NAME } from '../../config/app-config';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import ElasticSearch, { getElasticSuggestions } from './ElasticSearch';
import EnterpriseSearch, { getEnterpriseSuggestions } from './EnterpriseSearch';

const passthroughFilterOptions = (options) => options;
const getSuggestionLabel = (option) => (
    typeof option === 'string' ? option : option?.text || ''
);

const Search = (props) => {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const {
        selectedRelease,
        fetchArticle
    } = props;

    const usesEnterpriseSearch = SEARCH.provider === 'enterprise';
    const Provider = usesEnterpriseSearch ? EnterpriseSearch : ElasticSearch;
    const suggestionProvider = usesEnterpriseSearch ? getEnterpriseSuggestions : getElasticSuggestions;
    const suggestionDelay = usesEnterpriseSearch ? 120 : 300;
    const minimumSuggestionLength = usesEnterpriseSearch ? 2 : 1;

    const [query, setQuery] = useState('');
    const [submittedQuery, setSubmittedQuery] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [headerOpen, setHeaderOpen] = useState(false);
    const [innerOpen, setInnerOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [request, setRequest] = useState(null);
    const suggestionController = useRef(null);
    const suggestionSequence = useRef(0);

    useEffect(() => {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < minimumSuggestionLength) {
            suggestionController.current?.abort();
            suggestionSequence.current += 1;
            setSuggestions([]);
            return undefined;
        }

        const timeout = setTimeout(() => {
            suggestionController.current?.abort();
            const controller = new AbortController();
            suggestionController.current = controller;
            const sequence = ++suggestionSequence.current;

            suggestionProvider({ query: normalizedQuery, release: selectedRelease }, controller.signal)
                .then((items) => {
                    if (sequence !== suggestionSequence.current) return;
                    setSuggestions(Array.isArray(items) ? items : []);
                })
                .catch((error) => {
                    if (error?.name === 'AbortError') return;
                    if (sequence !== suggestionSequence.current) return;
                    console.error('Search suggestions failed:', error);
                    setSuggestions([]);
                });
        }, suggestionDelay);

        return () => clearTimeout(timeout);
    }, [
        minimumSuggestionLength,
        query,
        selectedRelease,
        suggestionDelay,
        suggestionProvider,
    ]);

    useEffect(() => () => suggestionController.current?.abort(), []);

    const submit = useCallback((value = query) => {
        const normalizedQuery = getSuggestionLabel(value).trim();
        if (!normalizedQuery) return;

        setQuery(normalizedQuery);
        setSubmittedQuery(normalizedQuery);
        setHeaderOpen(false);
        setInnerOpen(false);
        setDialogOpen(true);
        setRequest((current) => ({
            id: (current?.id || 0) + 1,
            query: normalizedQuery,
        }));
    }, [query]);

    const handleInputChange = useCallback((event, value, reason) => {
        if (!reason || ['input', 'clear'].includes(reason)) setQuery(value || '');
    }, []);

    const handleKeyDown = useCallback((event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        submit(event.target.value);
    }, [submit]);

    const inputBackground = theme.palette.background.paper;
    const inputColor = darkMode ? '#fff' : 'black';

    const renderSearchInput = useCallback((params, placeholder, autoFocus = false) => (
        <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            size="small"
            fullWidth
            autoFocus={autoFocus}
            onKeyDown={handleKeyDown}
            InputProps={{
                ...params.InputProps,
                startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon sx={{ ml: 0.75, color: inputColor }} />
                    </InputAdornment>
                ),
                sx: {
                    py: '6px',
                    fontFamily: FONTS.body,
                    color: inputColor,
                },
            }}
            sx={{
                bgcolor: inputBackground,
                borderRadius: 1,
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? '#444' : undefined,
                },
            }}
        />
    ), [darkMode, handleKeyDown, inputBackground, inputColor]);

    const commonAutocompleteProps = {
        freeSolo: true,
        value: null,
        inputValue: query,
        options: suggestions,
        getOptionLabel: getSuggestionLabel,
        filterOptions: passthroughFilterOptions,
        onInputChange: handleInputChange,
        onChange: (event, value) => value && submit(value),
    };

    return (
        <>
            <Autocomplete
                {...commonAutocompleteProps}
                fullWidth
                open={headerOpen && suggestions.length > 0}
                onOpen={() => setHeaderOpen(true)}
                onClose={() => setHeaderOpen(false)}
                renderInput={(params) => renderSearchInput(params, 'Search…')}
            />

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullScreen
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
                disableAutoFocus
            >
                <DialogTitle
                    disableTypography
                    sx={{
                        m: 0,
                        p: 0,
                        flexShrink: 0,
                        bgcolor: theme.palette.primary.main,
                        color: 'common.white',
                    }}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                        <Box display="flex" alignItems="center">
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{ fontFamily: FONTS.heading, fontWeight: 700, mr: 1 }}
                            >
                                {DISPLAY_NAME}
                            </Typography>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{ fontFamily: FONTS.heading, fontWeight: 300 }}
                            >
                                Search
                            </Typography>
                        </Box>
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="Close search"
                            onClick={() => setDialogOpen(false)}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 1,
                        display: 'flex',
                        flex: 1,
                        minHeight: 0,
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 1200,
                            mx: 'auto',
                            px: { xs: 2, md: 0 },
                            py: 2,
                            flexShrink: 0,
                        }}
                    >
                        <Autocomplete
                            {...commonAutocompleteProps}
                            open={innerOpen && suggestions.length > 0}
                            onOpen={() => setInnerOpen(true)}
                            onClose={() => setInnerOpen(false)}
                            renderInput={(params) => renderSearchInput(params, 'Start new search…', true)}
                        />
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: { xs: 2, md: 0 } }}>
                        <Provider
                            {...props}
                            request={request}
                            submittedQuery={submittedQuery}
                            onCloseSearch={() => setDialogOpen(false)}
                            fetchArticle={fetchArticle}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Search;
